import { useEffect, useState, useSyncExternalStore } from "react";
import { useIsAuthed } from "../lib/omauth.js";

/**
 * First-run welcome tour — a short stepped modal that explains the controls up top (trading
 * mode, connected venues + the dot legend, odds format, and where to see what your key can do).
 * Auto-shows once per browser after sign-in; replayable from the "?" in the top bar.
 *
 * It mocks each control inline (matching the real styles) rather than anchoring to live DOM,
 * so it's robust regardless of layout — the copy points you "up top" to the real thing.
 */

const SEEN_KEY = "gb.tour.seen";
function markSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* ignore */
  }
}
export function tourSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

/* re-open signal (top-bar "?" → showTour()) */
let tick = 0;
const listeners = new Set<() => void>();
export function showTour(): void {
  tick++;
  for (const l of listeners) l();
}
function useTourSignal(): number {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => tick,
  );
}

interface Step {
  title: string;
  body: React.ReactNode;
  visual?: React.ReactNode;
}

const STEPS: Step[] = [
  {
    title: "Welcome to greenbook",
    body: (
      <>
        A trading desk on the OpenMarkets network. Here’s a 60-second tour of the controls up top —
        you can replay it anytime from the <Kbd>?</Kbd> in the corner.
      </>
    ),
  },
  {
    title: "Paper vs Real — the switch that runs everything",
    body: (
      <>
        <b className="text-blue">Paper</b> (the default) lets you trade <i>any</i> price risk-free —
        it books an internal play book, so you can explore and practice even with no venues
        connected. <b className="text-down">Real</b> routes real money to the venues you’ve
        connected. Flip it in the top bar; the whole app follows.
      </>
    ),
    visual: (
      <div className="flex overflow-hidden rounded-md border border-border-2 text-[12px] font-semibold">
        <span className="bg-blue/20 px-3 py-1 text-blue">Paper</span>
        <span className="px-3 py-1 text-dim">Real</span>
      </div>
    ),
  },
  {
    title: "Your venues — where you can actually trade",
    body: (
      <>
        The chip up top adapts to your mode: in <b className="text-blue">Paper</b> it shows your{" "}
        <b>ATLAS play wallet</b>; in <b className="text-down">Real</b> it shows your live venue
        balances. <b>Anywhere</b> a venue appears, a dot tells you its state — paper works on any
        venue, <b>Real needs a 🟢</b>. Click the chip to see balances and connect or fund more.
      </>
    ),
    visual: (
      <div className="space-y-1.5 text-[12px]">
        <Legend dot="bg-up" label="Live — connected & funded (real orders work)" />
        <Legend dot="bg-amber" label="Connected, no balance — fund it to trade real" />
        <Legend dot="bg-faint" label="Not connected — paper only" />
      </div>
    ),
  },
  {
    title: "Odds format",
    body: (
      <>
        Prefer implied probability or American odds? Switch how every price shows — cents, %, or
        American — with the toggle up top.
      </>
    ),
    visual: (
      <div className="flex overflow-hidden rounded border border-border-2 text-[11px]">
        <span className="bg-amber/20 px-2 py-0.5 text-amber">¢</span>
        <span className="px-2 py-0.5 text-dim">%</span>
        <span className="px-2 py-0.5 text-dim">Am</span>
      </div>
    ),
  },
  {
    title: "What your key can do",
    body: (
      <>
        greenbook only uses the scopes your OpenMarkets plan granted. Click your key name’s{" "}
        <span className="inline-grid h-4 w-4 place-items-center rounded-full border border-border-2 text-[10px] text-dim">
          ⓘ
        </span>{" "}
        (top-right) anytime to see what it can do — trading, live data — and what an
        upgrade would unlock.
      </>
    ),
  },
  {
    title: "You’re set",
    body: (
      <>
        Head to <b>Markets</b>, pick a game, and click a best price to trade. Start in Paper; flip
        to Real when you’re ready. Have fun.
      </>
    ),
  },
];

export function WelcomeTour() {
  const authed = useIsAuthed();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const sig = useTourSignal();

  // Auto-open once after sign-in.
  useEffect(() => {
    if (authed && !tourSeen()) {
      setI(0);
      setOpen(true);
    }
  }, [authed]);

  // Replay from the "?" button.
  useEffect(() => {
    if (sig > 0) {
      setI(0);
      setOpen(true);
    }
  }, [sig]);

  if (!open) return null;
  const step = STEPS[i];
  const last = i === STEPS.length - 1;
  const close = () => {
    markSeen();
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 p-4" onClick={close}>
      <div
        className="max-h-full w-full max-w-md overflow-y-auto rounded-lg border border-border bg-panel-2 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-faint">
            Getting started · {i + 1}/{STEPS.length}
          </span>
          <button onClick={close} className="-m-2 p-2 text-faint hover:text-ink" title="Close">
            ✕
          </button>
        </div>

        <h2 className="text-lg font-semibold text-ink">{step.title}</h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-dim">{step.body}</p>
        {step.visual && <div className="mt-3 rounded-md border border-border bg-panel p-3">{step.visual}</div>}

        {/* progress dots */}
        <div className="mt-4 flex items-center gap-1.5">
          {STEPS.map((_, n) => (
            <span key={n} className={`h-1.5 rounded-full transition-all ${n === i ? "w-4 bg-up" : "w-1.5 bg-border-2"}`} />
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={close}
            className="min-h-10 text-[12px] text-faint hover:text-dim md:min-h-0"
          >
            {last ? "" : "Skip tour"}
          </button>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button
                onClick={() => setI((n) => Math.max(0, n - 1))}
                className="min-h-10 rounded border border-border-2 px-3 py-1.5 text-[12px] text-dim hover:bg-raised hover:text-ink md:min-h-0"
              >
                Back
              </button>
            )}
            <button
              onClick={() => (last ? close() : setI((n) => n + 1))}
              className="min-h-10 rounded bg-up/20 px-4 py-1.5 text-[12px] font-semibold text-up hover:bg-up/30 md:min-h-0"
            >
              {last ? "Start trading" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Legend({ dot, label }: { dot: string; label: string }) {
  return (
    <div className="flex items-center gap-2 text-dim">
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
      <span>{label}</span>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-grid h-4 w-4 place-items-center rounded border border-border-2 text-[10px] text-dim">
      {children}
    </span>
  );
}
